import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCurrentUser } from '../util/amplifyAuth';
import { useUserFiles } from '../context/UserFilesProvider';
import { useFilters } from '../context/FilterProvider';
import { useScreen } from '../context/ScreenProvider';
import { useCachedTestNameMap, useCachedFxRange } from '../hooks/useCachedData';
import { filterAndSortBloodTests, formatDate, aggregateLabTests, normalizeAggregatedResults } from '../util/GenericUtil';
import { groupFilesByDraw, formatDrawLabel } from '../util/DrawGroupUtil';
import { reconcileMerges } from '../util/labMerge';
import { parseDate } from '../../lambda/shared-utils/bin-config.mjs';
import { getCachedEditTests, getCachedNewTest } from '../util/CacheUtil';
import { retrieveUserFiles } from '../components/API';
import bloodGroupings from '../../lambda/health-recommendations/blood_groupings.json';
import TestChartEntry from '../components/TestChartEntry/TestChartEntry';
import NewTestForm from '../components/NewTestForm/NewTestForm';
import Spinner from '../components/Spinner/Spinner';
import { OrganIcon } from '../components/OrganSystemCircle/OrganIcons';
import '../styles/TestDetails.css';

/**
 * TestDetails - Full page for viewing all test results.
 * On mobile, redirects to Home with inline test details.
 * On desktop, renders the standalone test details page.
 */
function TestDetails() {
    const { isMobile } = useScreen();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const { userFiles, setUserFiles } = useUserFiles();
    const { normalizeTestName } = useCachedTestNameMap();
    const { getFxRange } = useCachedFxRange();

    const [user, setUser] = useState(undefined);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const {
        selectedSystem: categoryFilter, selectedFile, tags,
        selectSystem, forceSelectSystem, selectFile, setTags,
        clearFilters: clearContextFilters,
    } = useFilters();

    const [cleanedFiles, setCleanedFiles] = useState(undefined);
    const [uploadingState, setUploadingState] = useState(false);
    const [editTests, setEditTests] = useState(null);
    const [newTest, setNewTest] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [dateRange, setDateRange] = useState('all');

    // On mobile, redirect to Home with inline test details.
    // Keep hooks order stable by redirecting in an effect instead of early-returning before all hooks.
    useEffect(() => {
        if (!isMobile) return;
        const system = searchParams.get('system');
        const params = new URLSearchParams({ showTests: 'true' });
        if (system) params.set('system', system);
        navigate(`/home?${params.toString()}`, { replace: true });
    }, [isMobile, navigate, searchParams]);

    // Read ?system= query param on mount — set context if URL specifies a system
    useEffect(() => {
        if (isMobile) return;
        const systemParam = searchParams.get('system');
        if (systemParam && bloodGroupings[systemParam] && categoryFilter !== systemParam) {
            forceSelectSystem(systemParam);
        }
    }, [isMobile, searchParams, categoryFilter, forceSelectSystem]);

    // Auth check
    useEffect(() => {
        getCurrentUser()
            .then(data => {
                const cachedNewTest = getCachedNewTest();
                if (cachedNewTest) setNewTest(cachedNewTest);
                const cachedEditTests = getCachedEditTests();
                setEditTests(cachedEditTests || {});
                setUser(data);
            })
            .catch(() => setUser(null));
    }, []);

    // Redirect if not authenticated
    useEffect(() => {
        if (user === null) {
            navigate('/');
        }
    }, [user, navigate]);

    // Load user files (retrieveUserFiles checks FileCacheUtil internally)
    useEffect(() => {
        if (user && !userFiles) {
            retrieveUserFiles(setLoadingFiles, normalizeTestName)
                .then(res => { if (res) setUserFiles(res); });
        }
    }, [user, userFiles, setUserFiles, normalizeTestName]);

    // Clean files when userFiles changes, collapsing same-draw panel sheets into
    // one entry so each pill is a draw rather than a source document.
    //
    // reconcileMerges runs first, exactly as useBloodTestFiles does it. During the
    // window between a merged document being uploaded and its sources being
    // deleted, both copies are present; hiding the superseded sources is the
    // correctness step, and this route flattens bodies too, so skipping it counted
    // the same marker twice here while the dashboard was right.
    useEffect(() => {
        const blood = filterAndSortBloodTests(userFiles);
        const { visible } = reconcileMerges(blood, normalizeTestName);
        setCleanedFiles(groupFilesByDraw(visible, normalizeTestName));
    }, [userFiles, normalizeTestName]);

    // Date-filtered cleaned files
    const dateFilteredFiles = useMemo(() => {
        if (!cleanedFiles || dateRange === 'all') return cleanedFiles;

        const now = new Date();
        let cutoff;
        switch (dateRange) {
            case '3m': cutoff = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()); break;
            case '6m': cutoff = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()); break;
            case '1y': cutoff = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()); break;
            default: return cleanedFiles;
        }

        return cleanedFiles.filter(file => {
            // testdate is a YYYYMMDD integer — new Date(20260902) reads it as epoch
            // milliseconds, so parse it explicitly.
            const testDate = parseDate(file.data?.header?.testdate);
            if (!testDate) return true; // Include files without dates
            return testDate >= cutoff;
        });
    }, [cleanedFiles, dateRange]);

    // Filtered tests (no alphabetical sort — ordering is per-group by JSON marker position)
    const filteredTests = useMemo(() => {
        const files = dateFilteredFiles;
        if (!files) return [];

        let filtered = [];
        if (selectedFile === 'all') {
            files.forEach(file => {
                const testsWithDate = file.data.body.map(test => ({
                    ...test,
                    testdate: file.data.header.testdate
                }));
                filtered.push(...testsWithDate);
            });
            filtered = aggregateLabTests(filtered, normalizeTestName).map(normalizeAggregatedResults);
        } else if (typeof selectedFile === 'number' && files[selectedFile]) {
            filtered = files[selectedFile]?.data?.body || [];
        }

        // Apply system filter
        if (categoryFilter && bloodGroupings[categoryFilter]) {
            const systemMarkers = Object.keys(bloodGroupings[categoryFilter].markers);
            filtered = filtered.filter(test => {
                const testName = test.alias || test.test_name;
                return systemMarkers.includes(testName);
            });
        }

        // Apply search filter
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            filtered = filtered.filter(test => {
                const name = (test.alias || test.test_name || '').toLowerCase();
                return name.includes(q);
            });
        }

        return filtered;
    }, [dateFilteredFiles, selectedFile, normalizeTestName, categoryFilter, searchQuery]);

    // Group tests by organ system, ordered by JSON marker position
    const systemGroupedTests = useMemo(() => {
        const groups = {};
        const systemKeys = categoryFilter
            ? [categoryFilter]
            : Object.keys(bloodGroupings);

        // Build a lookup from filtered tests for O(1) matching
        const testsByName = {};
        filteredTests.forEach(test => {
            const testName = test.alias || test.test_name;
            if (!testsByName[testName]) testsByName[testName] = test;
        });

        systemKeys.forEach(systemKey => {
            const system = bloodGroupings[systemKey];
            if (!system) return;
            const systemMarkers = Object.keys(system.markers);
            // Order by JSON marker position (confidence weight order)
            const orderedTests = systemMarkers
                .filter(marker => marker in testsByName)
                .map(marker => testsByName[marker]);
            if (orderedTests.length > 0) {
                groups[systemKey] = orderedTests;
            }
        });
        return groups;
    }, [filteredTests, categoryFilter]);

    const handleBack = () => {
        navigate('/home');
    };

    const clearFilters = () => {
        clearContextFilters();
        setSearchQuery('');
        setDateRange('all');
    };

    const updateEditTest = (newValue) => {
        if (newValue == null) {
            setEditTests(prev => {
                const copy = { ...prev };
                const files = dateFilteredFiles;
                delete copy[files[selectedFile].key];
                return copy;
            });
        } else {
            setEditTests(prev => ({
                ...prev,
                [dateFilteredFiles[selectedFile].key]: newValue
            }));
        }
    };

    const renderContent = () => {
        if (loadingFiles) {
            return (
                <div className="test-details-loading">
                    <Spinner size={32} color="#666" />
                    <p>Loading tests...</p>
                </div>
            );
        }

        if (selectedFile === 'new' && newTest) {
            return (
                <NewTestForm
                    selectFile={selectFile}
                    normalizeTestName={normalizeTestName}
                    newTest={newTest}
                    setNewTest={setNewTest}
                    edit={false}
                    userFiles={userFiles}
                    setUserFiles={setUserFiles}
                    setUploadingState={setUploadingState}
                    userId={user?.userId}
                />
            );
        }

        if (typeof selectedFile === 'number' && editTests && dateFilteredFiles[selectedFile]?.key in editTests) {
            return (
                <NewTestForm
                    selectFile={selectFile}
                    normalizeTestName={normalizeTestName}
                    newTest={editTests[dateFilteredFiles[selectedFile].key]}
                    setNewTest={updateEditTest}
                    edit={true}
                    userFiles={userFiles}
                    setUserFiles={setUserFiles}
                    setUploadingState={setUploadingState}
                    userId={user?.userId}
                />
            );
        }

        if (!filteredTests.length) {
            return (
                <div className="test-details-empty">
                    <p>No tests found</p>
                    {(categoryFilter || searchQuery) && (
                        <button onClick={clearFilters} className="clear-filters-btn">
                            Clear Filters
                        </button>
                    )}
                </div>
            );
        }

        return Object.entries(systemGroupedTests).map(([systemKey, tests]) => {
            const system = bloodGroupings[systemKey];
            return (
                <div key={systemKey} className="organ-system-group" data-system={systemKey}>
                    <div className="organ-system-group-header">
                        <OrganIcon icon={system.icon} />
                        <span>{system.display_name}</span>
                    </div>
                    {tests.map((test, i) => (
                        <TestChartEntry
                            key={`${systemKey}-${i}-${selectedFile}`}
                            test={test}
                            tags={tags}
                            aggregate={selectedFile === 'all'}
                        />
                    ))}
                </div>
            );
        });
    };

    const hasActiveFilters = tags.length > 0 || categoryFilter || searchQuery.trim() || dateRange !== 'all';

    const activeFilterCount = [
        dateRange !== 'all',
        tags.length > 0,
        categoryFilter != null,
        searchQuery.trim().length > 0,
    ].filter(Boolean).length;

    if (isMobile) return null;

    return (
        <div className="test-details-page">
            {/* Header with back button */}
            <header className="test-details-header">
                <button className="back-button" onClick={handleBack}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1>Test Results</h1>
            </header>

            {/* Date tabs - horizontal scroll */}
            <div className="test-details-tabs">
                <button
                    className={`tab-pill ${selectedFile === 'all' ? 'selected' : ''}`}
                    onClick={() => selectFile('all')}
                >
                    All Tests
                </button>
                {dateFilteredFiles?.map((file, i) => (
                    <button
                        key={i}
                        className={`tab-pill ${selectedFile === i ? 'selected' : ''}`}
                        onClick={() => selectFile(i)}
                    >
                        {formatDrawLabel(file, formatDate)}
                    </button>
                ))}
                {newTest && (
                    <button
                        className={`tab-pill new-test ${selectedFile === 'new' ? 'selected' : ''}`}
                        onClick={() => selectFile('new')}
                    >
                        New Test
                    </button>
                )}
            </div>

            {/* Filters */}
            {selectedFile !== 'new' && (
                <>
                    {/* Search input */}
                    <div className="test-details-search">
                        <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <path d="M21 21l-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Search biomarkers..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                        {searchQuery && (
                            <button className="search-clear" onClick={() => setSearchQuery('')}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* Organ system icon filters */}
                    <div className="system-icon-filters">
                        {Object.entries(bloodGroupings).map(([key, system]) => (
                            <button
                                key={key}
                                className={`system-icon-btn ${categoryFilter === key ? 'active' : ''}`}
                                onClick={() => selectSystem(key)}
                            >
                                <OrganIcon icon={system.icon} />
                                <span>{system.display_name}</span>
                            </button>
                        ))}
                    </div>

                    {/* Combined date + status filters */}
                    <div className="combined-filters">
                        <div className="filter-scroll-row">
                            {[
                                { value: 'all', label: 'All' },
                                { value: '3m', label: '3M' },
                                { value: '6m', label: '6M' },
                                { value: '1y', label: '1Y' }
                            ].map(opt => (
                                <button
                                    key={opt.value}
                                    className={`filter-chip date ${dateRange === opt.value ? 'active' : ''}`}
                                    onClick={() => setDateRange(opt.value)}
                                >
                                    {opt.label}
                                </button>
                            ))}

                            <span className="filter-divider" />

                            <button
                                className={`filter-chip status-chip optimal ${tags.includes('optimal') ? '' : 'inactive'}`}
                                onClick={() => setTags(tags.includes('optimal')
                                    ? tags.filter(t => t !== 'optimal')
                                    : [...tags, 'optimal'])}
                            >
                                Optimal
                            </button>
                            <button
                                className={`filter-chip status-chip dysfunctional ${
                                    tags.includes('elevated') || tags.includes('diminished') ? '' : 'inactive'
                                }`}
                                onClick={() => {
                                    const newTags = new Set(tags);
                                    if (newTags.has('elevated') || newTags.has('diminished')) {
                                        newTags.delete('elevated');
                                        newTags.delete('diminished');
                                    } else {
                                        newTags.add('elevated');
                                        newTags.add('diminished');
                                    }
                                    setTags([...newTags]);
                                }}
                            >
                                Dysfunctional
                            </button>
                            <button
                                className={`filter-chip status-chip abnormal ${
                                    tags.includes('high') || tags.includes('low') ? '' : 'inactive'
                                }`}
                                onClick={() => {
                                    const newTags = new Set(tags);
                                    if (newTags.has('high') || newTags.has('low')) {
                                        newTags.delete('high');
                                        newTags.delete('low');
                                    } else {
                                        newTags.add('high');
                                        newTags.add('low');
                                    }
                                    setTags([...newTags]);
                                }}
                            >
                                Abnormal
                            </button>

                            {hasActiveFilters && (
                                <button className="clear-all-btn" onClick={clearFilters}>
                                    Clear ({activeFilterCount})
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Test list */}
            <div className="test-details-list">
                {renderContent()}
            </div>
        </div>
    );
}

export default TestDetails;
